import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, ArrowRight, Plus, X, Check, AlertTriangle, ChevronDown } from 'lucide-react';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { AprTrustBlock } from '../components/apr/AprTrustBlock';

const PEPTIDES = [
  {
    id: 'bpc157',
    name: 'BPC-157',
    fullName: 'Body Protection Compound-157',
    category: 'Recovery & Healing',
    color: '#22c55e',
    guideUrl: '/blogs/article/bpc-157-dosage-guide',
    buyUrl: 'https://www.americanpeptideresearch.net/product/bpc-157-10mg/ref/16/',
    dosing: { range: '250-500 mcg/day', frequency: '1-2x daily', route: 'Subcutaneous or Oral', cycle: '4-12 weeks' },
    effects: ['Tendon & ligament repair', 'Gut healing (IBS, leaky gut)', 'Reduced inflammation', 'Neuroprotective', 'Accelerated wound healing'],
    onset: '3-7 days for noticeable effects',
    cost: '$40-70/vial (5mg)',
    costPerMonth: '$40-140',
    safety: { rating: 5, notes: 'Excellent safety profile. One of the most studied and well-tolerated peptides. Minimal side effects reported.' },
    bestFor: ['Injury recovery', 'Gut health', 'Joint pain', 'Post-surgery healing'],
    stacksWith: ['TB-500', 'GHK-Cu'],
    bloodwork: 'Basic panel recommended but not critical',
  },
  {
    id: 'tb500',
    name: 'TB-500',
    fullName: 'Thymosin Beta-4 Fragment',
    category: 'Recovery & Healing',
    color: '#3b82f6',
    guideUrl: '/blogs/article/tb500-heart-health-cardiac-repair',
    buyUrl: 'https://www.americanpeptideresearch.net/product/tb-500-10mg/ref/16/',
    dosing: { range: '2-5 mg/week', frequency: '2-3x per week', route: 'Subcutaneous', cycle: '4-8 weeks loading, then maintenance' },
    effects: ['Systemic tissue repair', 'Cardiac tissue healing', 'Hair regrowth potential', 'Anti-inflammatory', 'Promotes angiogenesis'],
    onset: '2-4 weeks for full effects',
    cost: '$50-80/vial (5mg)',
    costPerMonth: '$100-200',
    safety: { rating: 4, notes: 'Well-tolerated. Some users report temporary head rush or fatigue post-injection. Avoid with active cancer.' },
    bestFor: ['Chronic injuries', 'Heart health', 'Systemic inflammation', 'Athletic recovery'],
    stacksWith: ['BPC-157', 'GHK-Cu'],
    bloodwork: 'CBC + CMP recommended',
  },
  {
    id: 'retatrutide',
    name: 'Retatrutide',
    fullName: 'Triple Agonist (GLP-1/GIP/Glucagon)',
    category: 'Fat Loss & Metabolic',
    color: '#f59e0b',
    guideUrl: '/blogs/article/retatrutide-longevity-weapon-epigenetic-reversal-cancer-defense',
    buyUrl: 'https://www.americanpeptideresearch.net/product/reta-30mg/ref/16/',
    dosing: { range: '1-12 mg/week (titrate up)', frequency: '1x weekly', route: 'Subcutaneous', cycle: '12-24+ weeks' },
    effects: ['Dramatic fat loss (up to 24% body weight)', 'Appetite suppression', 'Improved insulin sensitivity', 'Potential longevity benefits', 'Metabolic optimization'],
    onset: '1-2 weeks appetite changes, 4-8 weeks visible fat loss',
    cost: '$60-100/vial (5mg)',
    costPerMonth: '$120-400',
    safety: { rating: 3, notes: 'Common GI side effects (nausea, diarrhea) especially during titration. Start low and go slow. Requires medical supervision.' },
    bestFor: ['Significant fat loss', 'Metabolic syndrome', 'Body recomposition', 'Obesity management'],
    stacksWith: ['BPC-157 (for GI support)'],
    bloodwork: 'Full metabolic panel, HbA1c, thyroid required',
  },
  {
    id: 'cjc1295',
    name: 'CJC-1295 + Ipamorelin',
    fullName: 'GHRH + GHRP Combo',
    category: 'Growth Hormone',
    color: '#a78bfa',
    guideUrl: '/blogs/article/cjc-1295-with-dac-ipamorelin-how-these-2-peptides-rebuild-your-body-from-the-inside-out',
    buyUrl: 'https://www.americanpeptideresearch.net/ref/16/',
    dosing: { range: '100-300 mcg each/day', frequency: '1-2x daily (before bed ideal)', route: 'Subcutaneous', cycle: '8-16 weeks, cycle off 4 weeks' },
    effects: ['Increased GH & IGF-1', 'Improved sleep quality', 'Enhanced recovery', 'Lean muscle support', 'Anti-aging benefits'],
    onset: '2-4 weeks for sleep improvements, 6-12 weeks for body comp',
    cost: '$50-80/vial (5mg each)',
    costPerMonth: '$100-200',
    safety: { rating: 4, notes: 'Well-studied combination. Possible water retention, tingling at injection site, increased hunger. Monitor IGF-1 levels.' },
    bestFor: ['Anti-aging', 'Sleep quality', 'Recovery enhancement', 'Natural GH optimization'],
    stacksWith: ['BPC-157', 'AOD-9604'],
    bloodwork: 'IGF-1, GH, fasting glucose, insulin required',
  },
  {
    id: 'aod9604',
    name: 'AOD-9604',
    fullName: 'Advanced Obesity Drug Fragment',
    category: 'Fat Loss & Metabolic',
    color: '#ef4444',
    guideUrl: '/blogs/article/aod-9604-fat-burning-peptide-guide',
    buyUrl: null,
    dosing: { range: '250-300 mcg/day', frequency: '1x daily (morning, fasted)', route: 'Subcutaneous', cycle: '8-12 weeks' },
    effects: ['Targeted fat metabolism', 'No impact on blood sugar', 'No IGF-1 increase', 'Cartilage repair potential', 'Bone strengthening'],
    onset: '4-8 weeks for noticeable fat loss',
    cost: '$50-70/vial (5mg)',
    costPerMonth: '$50-140',
    safety: { rating: 5, notes: 'Excellent safety profile. HGH fragment without systemic growth effects. No significant side effects reported in studies.' },
    bestFor: ['Stubborn fat loss', 'Athletes avoiding IGF-1 increase', 'Joint support', 'Body composition'],
    stacksWith: ['CJC-1295/Ipamorelin', 'BPC-157'],
    bloodwork: 'Basic panel, fasting glucose recommended',
  },
  {
    id: 'motsc',
    name: 'MOTS-C',
    fullName: 'Mitochondrial ORF of 12S rRNA Type-C',
    category: 'Longevity & Performance',
    color: '#06b6d4',
    guideUrl: '/blogs/article/mots-c-benefits',
    buyUrl: null,
    dosing: { range: '5-10 mg/week', frequency: '3-5x per week', route: 'Subcutaneous', cycle: '8-12 weeks' },
    effects: ['Exercise mimetic', 'Improved insulin sensitivity', 'Enhanced mitochondrial function', 'Potential lifespan extension', 'AMPK activation'],
    onset: '2-4 weeks for energy improvements',
    cost: '$70-120/vial (5mg)',
    costPerMonth: '$140-300',
    safety: { rating: 4, notes: 'Relatively new but promising safety data. Naturally occurring peptide. Some injection site discomfort reported.' },
    bestFor: ['Longevity optimization', 'Metabolic health', 'Endurance athletes', 'Aging population'],
    stacksWith: ['Epitalon', 'BPC-157'],
    bloodwork: 'Fasting glucose, insulin, HbA1c, mitochondrial panel',
  },
  {
    id: 'epitalon',
    name: 'Epitalon',
    fullName: 'Epithalon (AEDG peptide)',
    category: 'Anti-Aging & Longevity',
    color: '#ec4899',
    guideUrl: '/blogs/article/epitalon-telomere-anti-aging-peptide-guide',
    buyUrl: null,
    dosing: { range: '5-10 mg/day', frequency: 'Daily for 10-20 day cycles', route: 'Subcutaneous', cycle: '10-20 days, repeat every 4-6 months' },
    effects: ['Telomere lengthening', 'Melatonin regulation', 'Improved sleep architecture', 'Antioxidant activation', 'Potential lifespan extension'],
    onset: '10-20 days (end of cycle)',
    cost: '$50-80/vial (10mg)',
    costPerMonth: '$100-160 (during active cycle)',
    safety: { rating: 4, notes: 'Well-studied by Dr. Khavinson over 20+ years. Short cycles reduce risk. No major adverse effects in human trials.' },
    bestFor: ['Anti-aging protocols', 'Sleep optimization', 'Telomere health', 'Longevity stacking'],
    stacksWith: ['MOTS-C', 'GHK-Cu'],
    bloodwork: 'Telomere length test, melatonin levels optional',
  },
];

const MAX_COMPARE = 3;

const SafetyBadge = ({ rating }) => {
  const labels = { 5: 'Excellent', 4: 'Good', 3: 'Moderate', 2: 'Caution', 1: 'High Risk' };
  const colors = { 5: '#22c55e', 4: '#3b82f6', 3: '#f59e0b', 2: '#f97316', 1: '#ef4444' };
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="w-3 h-3 rounded-sm" style={{ background: i <= rating ? colors[rating] : 'rgba(255,255,255,0.1)' }} />
        ))}
      </div>
      <span className="text-xs font-heading uppercase tracking-wider" style={{ color: colors[rating] }}>{labels[rating]}</span>
    </div>
  );
};

const PeptideSelector = ({ selected, onSelect, onRemove }) => {
  const [open, setOpen] = useState(false);
  const available = PEPTIDES.filter(p => !selected.find(s => s.id === p.id));

  return (
    <div className="relative" data-testid="peptide-selector">
      {selected.length < MAX_COMPARE && (
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-5 py-3 border border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-[#E63946] transition-all w-full justify-center font-heading text-sm uppercase tracking-wider"
          data-testid="add-peptide-btn"
        >
          <Plus className="w-4 h-4" /> Add Peptide to Compare {selected.length > 0 && `(${selected.length}/${MAX_COMPARE})`}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 max-h-72 overflow-y-auto" data-testid="peptide-dropdown">
          {available.map(p => (
            <button
              key={p.id}
              onClick={() => { onSelect(p); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-800 transition-colors border-b border-zinc-800/50 last:border-0"
              data-testid={`select-${p.id}`}
            >
              <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ background: p.color }} />
              <div>
                <div className="text-white text-sm font-heading">{p.name}</div>
                <div className="text-zinc-500 text-xs">{p.category}</div>
              </div>
            </button>
          ))}
          {available.length === 0 && (
            <div className="px-4 py-3 text-zinc-500 text-sm text-center">All peptides selected</div>
          )}
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {selected.map(p => (
            <span key={p.id} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-heading uppercase tracking-wider border" style={{ borderColor: p.color, color: p.color }}>
              {p.name}
              <button onClick={() => onRemove(p.id)} className="hover:text-white transition-colors" data-testid={`remove-${p.id}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ComparisonTable = ({ peptides }) => {
  if (peptides.length < 2) return null;

  const rows = [
    { label: 'Full Name', key: 'fullName' },
    { label: 'Category', key: 'category' },
    { label: 'Dose Range', key: (p) => p.dosing.range },
    { label: 'Frequency', key: (p) => p.dosing.frequency },
    { label: 'Route', key: (p) => p.dosing.route },
    { label: 'Cycle Length', key: (p) => p.dosing.cycle },
    { label: 'Time to Effects', key: 'onset' },
    { label: 'Cost/Month', key: 'costPerMonth' },
    { label: 'Safety', key: 'safety', special: 'safety' },
    { label: 'Effects', key: 'effects', special: 'list' },
    { label: 'Best For', key: 'bestFor', special: 'list' },
    { label: 'Stacks With', key: 'stacksWith', special: 'list' },
    { label: 'Bloodwork Required', key: 'bloodwork' },
    { label: 'Safety Notes', key: (p) => p.safety.notes },
  ];

  return (
    <div className="overflow-x-auto mt-8" data-testid="comparison-table">
      <table className="w-full border-collapse min-w-[600px]">
        <thead>
          <tr>
            <th className="text-left p-4 text-xs font-heading uppercase tracking-widest text-zinc-500 border-b border-zinc-800 w-40">Attribute</th>
            {peptides.map(p => (
              <th key={p.id} className="p-4 text-center border-b border-l border-zinc-800" style={{ borderBottomColor: p.color, borderBottomWidth: '2px' }}>
                <div className="text-white font-heading text-lg">{p.name}</div>
                <div className="text-zinc-500 text-xs">{p.category}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.label} className={idx % 2 === 0 ? 'bg-zinc-900/20' : ''}>
              <td className="p-4 text-xs font-heading uppercase tracking-wider text-zinc-400 border-r border-zinc-800/50 align-top whitespace-nowrap">{row.label}</td>
              {peptides.map(p => (
                <td key={p.id} className="p-4 text-sm text-zinc-300 border-l border-zinc-800/30 align-top">
                  {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                  {row.special === 'safety' ? (
                    <SafetyBadge rating={p.safety.rating} />
                  ) : row.special === 'list' ? (
                    <ul className="space-y-1">
                      {(typeof row.key === 'function' ? row.key(p) : p[row.key]).map((item, i) => (
                        <li key={item} className="flex items-start gap-1.5 text-xs">
                          <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: p.color }} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span>{typeof row.key === 'function' ? row.key(p) : p[row.key]}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td className="p-4 text-xs font-heading uppercase tracking-wider text-zinc-400 border-r border-zinc-800/50">Links</td>
            {peptides.map(p => (
              <td key={p.id} className="p-4 border-l border-zinc-800/30">
                <div className="flex flex-col gap-2">
                  <Link to={p.guideUrl} className="text-xs uppercase tracking-wider font-heading hover:underline" style={{ color: p.color }} data-testid={`guide-link-${p.id}`}>
                    Full Guide &rarr;
                  </Link>
                  {p.buyUrl && (
                    <a href={p.buyUrl} target="_blank" rel="sponsored noopener" className="text-xs uppercase tracking-wider text-[#E63946] font-heading hover:underline" data-testid={`buy-link-${p.id}`}>
                      Buy (25% off w/ NINJA) &rarr;
                    </a>
                  )}
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const QuickComparePresets = ({ onSelect }) => {
  const presets = [
    { label: 'Recovery Stack', ids: ['bpc157', 'tb500'], desc: 'The classic healing duo' },
    { label: 'Fat Loss Battle', ids: ['retatrutide', 'aod9604'], desc: 'Two approaches to fat loss' },
    { label: 'Longevity Trio', ids: ['motsc', 'epitalon', 'bpc157'], desc: 'Anti-aging powerhouse' },
    { label: 'GH vs Recovery', ids: ['cjc1295', 'bpc157', 'tb500'], desc: 'Growth vs repair' },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="quick-presets">
      {presets.map(preset => (
        <button
          key={preset.label}
          onClick={() => onSelect(preset.ids.map(id => PEPTIDES.find(p => p.id === id)).filter(Boolean))}
          className="text-left p-4 border border-zinc-800 hover:border-[#E63946]/40 transition-all bg-zinc-900/20 group"
          data-testid={`preset-${preset.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="text-white text-sm font-heading group-hover:text-[#E63946] transition-colors">{preset.label}</div>
          <div className="text-zinc-500 text-xs mt-1">{preset.desc}</div>
          <div className="flex gap-1 mt-2">
            {preset.ids.map(id => {
              const p = PEPTIDES.find(pp => pp.id === id);
              return p ? <div key={id} className="w-2 h-2 rounded-full" style={{ background: p.color }} /> : null;
            })}
          </div>
        </button>
      ))}
    </div>
  );
};

const PeptideComparisonToolPage = () => {
  const [selected, setSelected] = useState([]);

  const handleSelect = (peptide) => {
    if (selected.length < MAX_COMPARE && !selected.find(s => s.id === peptide.id)) {
      setSelected([...selected, peptide]);
    }
  };

  const handleRemove = (id) => {
    setSelected(selected.filter(s => s.id !== id));
  };

  const handlePreset = (peptides) => {
    setSelected(peptides);
  };

  return (
    <main className="pt-20 min-h-screen" data-testid="peptide-comparison-page">
      <PageSEO
        title="Peptide Comparison Tool — Compare Peptides Side-by-Side | NinjAthlete"
        description="Compare peptides side-by-side: dosing, effects, cost, safety ratings. BPC-157, TB-500, Retatrutide, CJC-1295, AOD-9604, MOTS-C, and Epitalon."
        path="/tools/peptide-compare"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Tools', url: '/tools' }, { name: 'Peptide Comparison', url: '/tools/peptide-compare' }]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0a0a0a] to-black" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(230,57,70,0.5) 0%, transparent 50%)' }} />
        <div className="container-ninja relative z-10 py-14 md:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#E63946] text-white text-xs font-heading tracking-widest uppercase mb-5">
              <FlaskConical className="w-3.5 h-3.5" /> Interactive Tool
            </span>
            <h1 className="text-4xl sm:text-5xl text-white font-heading leading-[0.95] mb-4" data-testid="comparison-tool-title">
              PEPTIDE <span className="text-[#E63946]">COMPARISON</span> TOOL
            </h1>
            <p className="text-zinc-400 text-base md:text-lg max-w-xl mb-6">
              Select 2-3 peptides to compare side-by-side. Dosing protocols, effects, cost, safety profiles, and stacking compatibility — all in one view.
            </p>
            <div className="flex items-center gap-3 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-[#f59e0b]" /> For educational purposes only</span>
              <span>|</span>
              <Link to="/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide" className="text-[#E63946] hover:underline">
                Always get bloodwork first
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-[#050505] py-12 md:py-16">
        <div className="container-ninja">
          {/* SEO/AI-crawler discoverability: surface the APR affiliate URL in
              the initial DOM so bots see the .net /ref/16/ link without
              needing to interact with the peptide selectors. */}
          <AprTrustBlock variant="full" cta="Shop APR · 25% off" />

          {/* Quick Compare Presets */}
          <div className="mb-8">
            <p className="text-zinc-400 text-xs uppercase tracking-widest font-bold mb-4">Quick Compare</p>
            <QuickComparePresets onSelect={handlePreset} />
          </div>

          {/* Selector */}
          <div className="mb-6">
            <p className="text-zinc-400 text-xs uppercase tracking-widest font-bold mb-3">Or Build Your Own Comparison</p>
            <PeptideSelector selected={selected} onSelect={handleSelect} onRemove={handleRemove} />
          </div>

          {/* Empty State */}
          {selected.length < 2 && (
            <div className="text-center py-16 border border-dashed border-zinc-800 mt-8" data-testid="empty-state">
              <FlaskConical className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-sm mb-2">Select at least 2 peptides to compare</p>
              <p className="text-zinc-600 text-xs">Use the presets above or add peptides manually</p>
            </div>
          )}

          {/* Comparison Table */}
          <ComparisonTable peptides={selected} />

          {/* Disclaimer */}
          {selected.length >= 2 && (
            <div className="mt-8 p-5 border border-[#f59e0b]/20 bg-[#f59e0b]/5" data-testid="disclaimer-box">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-zinc-300 text-sm font-medium mb-1">Medical Disclaimer</p>
                  <p className="text-zinc-400 text-xs leading-relaxed">
                    This comparison tool is for educational and research purposes only. Peptides are not FDA-approved for human use. Always consult a qualified healthcare provider and obtain comprehensive{' '}
                    <Link to="/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide" className="text-[#E63946] hover:underline">
                      pre-protocol bloodwork
                    </Link>{' '}
                    before beginning any peptide protocol.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-12 md:py-16 border-t border-zinc-900">
        <div className="container-ninja text-center">
          <h2 className="text-white font-heading text-2xl md:text-3xl mb-4">Explore More Peptide Resources</h2>
          <p className="text-zinc-400 max-w-md mx-auto mb-6 text-sm">
            Use code <strong className="text-[#FFD100]">NINJA</strong> for 25% off research-grade peptides.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/peptides" className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
              Peptide Hub <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/tools/peptide-stack" className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 font-heading text-sm uppercase tracking-wider hover:border-white hover:text-white transition-colors">
              Stack Builder <FlaskConical className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PeptideComparisonToolPage;
